import * as React from 'react';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import Box from '@mui/material/Box';
import { ListItem } from '@mui/material';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import { RoomTag, RoomTaggingLabels } from './ImageProcessingRoomTagging';

type CustomMultipleSelect = {
    onAddRoomTags: (event: SelectChangeEvent) => void;
    roomTags: string[];
    onDeleteRoomTag: (tagToDelete: string) => void;
    availableRoomCategories: RoomTag[];
    labels: RoomTaggingLabels;
};

const CustomMultipleSelect = React.forwardRef(
    (
        {
            onAddRoomTags,
            roomTags,
            onDeleteRoomTag,
            availableRoomCategories,
            labels
        }: CustomMultipleSelect,
        ref
    ) => {
        return (
            <FormControl sx={{ width: '100%', marginTop: '32px' }}>
                <InputLabel
                    id="demo-multiple-chip-label"
                    htmlFor="select-multiple-chip"
                    sx={{ backgroundColor: 'white', paddingRight: '4px' }}
                >
                    {labels.roomTaggingInputLabel}
                </InputLabel>
                <Select
                    displayEmpty={true}
                    onChange={onAddRoomTags}
                    multiple
                    input={
                        <OutlinedInput
                            ref={ref}
                            id="select-multiple-chip"
                            label="Chip"
                            sx={{
                                '&& .MuiInputBase-input': {
                                    minHeight: '32px',
                                    width: '100%',
                                    height: '100%'
                                }
                            }}
                        />
                    }
                    value={roomTags}
                    renderValue={(selected) => (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                gap: 0.5
                            }}
                        >
                            {selected.map((value: string, index: number) => {
                                let roomTag = availableRoomCategories.find(
                                    (currentTag) => currentTag.value === value
                                );

                                return (
                                    <ListItem key={value} sx={{ width: 'auto', padding: '2px' }}>
                                        <Chip
                                            key={value}
                                            label={
                                                roomTag ? labels.roomTag(roomTag.displayName) : ''
                                            }
                                            variant={'outlined'}
                                            onDelete={() => onDeleteRoomTag(value)}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                        />
                                        {index === selected.length - 1 ? (
                                            <Typography
                                                sx={{
                                                    margin: 'auto',
                                                    paddingLeft: '8px',
                                                    color: 'rgba(0, 0, 0, 0.26)',
                                                    fontSize: '1.6rem',
                                                    fontWeight: 600
                                                }}
                                            >
                                                {labels.roomTaggingAddTag}
                                            </Typography>
                                        ) : null}
                                    </ListItem>
                                );
                            })}
                        </Box>
                    )}
                    MenuProps={{
                        anchorEl: ref.current,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                        },

                        PaperProps: {
                            sx: {
                                '&& .MuiList-root': {
                                    maxHeight: '30vh',
                                    with: 'inherit'
                                },

                                '&& .MuiMenuItem-root': {
                                    padding: 2,
                                    fontSize: '16px',
                                    alignItems: 'center'
                                },
                                '&& .MuiInputLabel-root': {
                                    display: 'flex',
                                    justifyContent: 'center'
                                }
                            }
                        }
                    }}
                >
                    {availableRoomCategories.map((tag) => (
                        <MenuItem key={tag.value} value={tag.value}>
                            {labels.roomTag(tag.displayName)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }
);

export default CustomMultipleSelect;
